import { useState } from 'react';
import cn from 'classnames';

import { OPopper, OResponsiveModal } from '@src/shared';

import { formatTimeRange } from '@src/utils';

import { IScheduledMeeting, TToggleView } from '@src/types';
import { MEETING_TYPE } from '@src/constants';

import useMeetingCard from './useMeetingCard';

import MeetingIcon from '@src/assets/icons/MeetingIcon';
import { JoinIcon, WebinarIcon } from '@src/assets/icons';

import { BiDotsHorizontalRounded } from 'react-icons/bi';
import { FaEdit, FaRegTrashAlt, FaRegUser } from 'react-icons/fa';
import { FaUserGroup } from 'react-icons/fa6';
import { FiClock } from 'react-icons/fi';
import { IoShareSocialSharp } from 'react-icons/io5';

import DeleteModal from '@pages/WebinarAttendees/Components/DeleteModal';

import styles from './meetingCard.module.scss';
import { isWebView } from '@src/helpers';
import { sendEventToWebView } from '@src/bridge/bridgeMessenger';
import { EWebViewEvents } from '@src/bridge/types';

interface IMeetingCardProps {
  meeting: IScheduledMeeting;
  isActive: boolean;
  onInvite: (link: string) => void;
  view: TToggleView;
}

const MeetingCard = ({ meeting, isActive, onInvite, view }: IMeetingCardProps) => {
  const {
    inviteLink,
    updateLink,
    isMobileDevice,
    isOptionsOpen,
    setIsOptionsOpen,
    prefixedTitle,
    joinWebinar,
    navigate,
  } = useMeetingCard({
    meeting,
  });
  const [openConfirmModal, setOpenConfirmModal] = useState(false);

  const viewClassName = styles[`${isMobileDevice ? 'list' : view}-view`];

  return (
    <div id="meeting-card" className={styles['meeting-card']}>
      {isMobileDevice && (
        <div
          className={cn(styles['menu-dots'], styles['menu-dots-mobile'], {
            [styles['activeDots']]: isOptionsOpen || isActive,
          })}
          onClick={() => {
            setIsOptionsOpen(true);
          }}
        >
          <BiDotsHorizontalRounded size={24} />
        </div>
      )}
      <div id="meeting-card-header" className={cn(styles['meeting-card-header'], viewClassName)}>
        <div id="meeting-card-header-left" className={cn(styles['meeting-card-header-left'], viewClassName)}>
          <div id="meeting-icon" className={styles['meeting-icon']}>
            {meeting.type === MEETING_TYPE.WEBINAR ? <WebinarIcon /> : <MeetingIcon />}
          </div>

          <div id="meeting-info" className={styles['meeting-info']}>
            <div className={cn(styles['meeting-title'], viewClassName)}>{prefixedTitle}</div>
            <div id="time-range" className={styles['time-range']}>
              <FiClock />
              <span> {formatTimeRange(meeting.startTime, meeting.endTime)}</span>
            </div>
          </div>
        </div>
        <div id="meeting-card-header-right" className={cn(styles['meeting-card-header-right'], viewClassName)}>
          <div id="meeting-card-header-attendees" className={styles['meeting-card-header-attendees']}>
            <span className={cn(styles['meeting-type-label'], styles[meeting.type.toLowerCase()])}>
              {meeting.type === MEETING_TYPE.MEETING ? MEETING_TYPE.MEETING : meeting.type}
            </span>
            <div id="attendees-count" className={styles['attendees-count']}>
              <FaRegUser size={16} />
              <span>{0}</span>
            </div>
          </div>

          {isMobileDevice ? (
            <>
              <OResponsiveModal isOpen={isOptionsOpen} title="More" onClose={() => setIsOptionsOpen(false)} hasBackdrop>
                <div className={styles.modalContent} id="webinar-invite-modal">
                  <ul className={styles.optionsList}>
                    <li
                      className={styles.optionItem}
                      onClick={() => {
                        if (meeting.type === MEETING_TYPE.WEBINAR) {
                          if (!isWebView()) {
                            navigate(updateLink);
                          } else {
                            const navigateTo = `/Meet${updateLink}`;
                            sendEventToWebView({
                              eventType: EWebViewEvents.NAVIGATE_TO,
                              payload: { url: navigateTo },
                            });
                            setIsOptionsOpen(false);
                          }
                        }
                      }}
                    >
                      <span className={styles.icon}>
                        <FaEdit />
                      </span>
                      <span>Edit</span>
                    </li>
                    {meeting.type === MEETING_TYPE.WEBINAR && (
                      <>
                        <li
                          className={styles.optionItem}
                          onClick={() => {
                            if (!isWebView()) {
                              navigate(`/webinar/${meeting.id}/attendee`);
                            } else {
                              const navigateTo = `/Meet/webinar/${meeting.id}/attendee`;
                              sendEventToWebView({
                                eventType: EWebViewEvents.NAVIGATE_TO,
                                payload: { url: navigateTo },
                              });
                              setIsOptionsOpen(false);
                            }
                          }}
                        >
                          <span className={styles.icon}>
                            <FaUserGroup />
                          </span>
                          <span>Manage Attendees</span>
                        </li>
                        <li
                          className={styles.optionItem}
                          onClick={() => {
                            onInvite(inviteLink);
                            setIsOptionsOpen(false);
                          }}
                        >
                          <span className={styles.icon}>
                            <IoShareSocialSharp />
                          </span>
                          <span>Invite to webinar</span>
                        </li>
                        <li className={styles.optionItem} onClick={joinWebinar}>
                          <span className={styles.icon}>
                            <img src={JoinIcon} alt="Join icon" />
                          </span>
                          <span>Join Webinar</span>
                        </li>
                        <li
                          className={`${styles.optionItem} ${styles.delete}`}
                          onClick={() => {
                            setOpenConfirmModal(true);
                            setIsOptionsOpen(false);
                          }}
                        >
                          <span className={styles.icon}>
                            <FaRegTrashAlt className={`${styles['danger']}`} />
                          </span>
                          <span>Delete Webinar</span>
                        </li>
                      </>
                    )}
                  </ul>
                </div>
              </OResponsiveModal>
            </>
          ) : null}
        </div>
      </div>
      {!isMobileDevice && (
        <OPopper
          customStyles={{ root: { border: '1px solid #CCD3D7', width: 'auto' } }}
          position="top-end"
          offset={isMobileDevice ? [0, 0] : [16, -4]}
          classes={{ containerStyle: styles['meeting-dots-container'] }}
          icon={
            <div className={cn(styles['menu-dots'], { [styles['activeDots']]: isOptionsOpen })}>
              <BiDotsHorizontalRounded size={24} />
            </div>
          }
          bgColor={'white'}
          onOpen={() => setIsOptionsOpen(true)}
          onClose={() => setIsOptionsOpen(false)}
          isActive={isActive}
        >
          <div id="meeting-card-menu" className={styles['meeting-card-menu']}>
            <button
              id="edit-button"
              className={styles['menu-item']}
              type="button"
              onClick={() => {
                if (meeting.type === MEETING_TYPE.WEBINAR) {
                  navigate(updateLink);
                }
              }}
            >
              <FaEdit />
              Edit
            </button>
            {meeting.type === MEETING_TYPE.WEBINAR && (
              <>
                <button
                  id="manage-attendees-button"
                  className={styles['menu-item']}
                  type="button"
                  onClick={() => {
                    navigate(`/webinar/${meeting.id}/attendee`);
                  }}
                >
                  <FaUserGroup /> Manage Attendees
                </button>
                <button
                  id="invite-to-webinar-button"
                  className={styles['menu-item']}
                  type="button"
                  onClick={() => onInvite(inviteLink)}
                >
                  <IoShareSocialSharp />
                  Invite to webinar
                </button>

                <button id="join-webinar-button" className={styles['menu-item']} type="button" onClick={joinWebinar}>
                  <img src={JoinIcon} alt="Join icon" /> Join Webinar
                </button>

                <button
                  id="delete-webinar-button"
                  className={`${styles['menu-item']} ${styles['danger']}`}
                  type="button"
                  onClick={() => setOpenConfirmModal(true)}
                >
                  <FaRegTrashAlt className={`${styles['danger']}`} />
                  Delete webinar
                </button>
              </>
            )}
          </div>
        </OPopper>
      )}
      <DeleteModal
        openConfirmModal={openConfirmModal}
        onClose={() => setOpenConfirmModal(false)}
        meetingId={meeting.id}
      />
    </div>
  );
};

export default MeetingCard;
